"use strict";
// Copyright (c) Microsoft Corporation. All rights reserved.
var _a, _b, _c, _d, _e;
Object.defineProperty(exports, "__esModule", { value: true });
exports.PollingModeManager = void 0;
const types_1 = require("../../types");
const foundation_1 = require("../foundation");
const gamepadEvents_1 = require("../gamepadEvents");
const useRegisterInputHook_1 = require("../../hooks/inner-utility/useRegisterInputHook");
const inputModeManager_1 = require("./inputModeManager");
const input_manager_1 = require("./input-manager");
const input_processors_1 = require("../input/input-processors");
const POLLING_MODES = [types_1.PollingMode.None, types_1.PollingMode.Callbacks, types_1.PollingMode.Navigation, types_1.PollingMode.All];
const consolePrefix = '[PollingModeManager]';
class PollingModeRequestTracker {
    constructor() {
        this[_a] = new Set();
        this[_b] = new Set();
        this[_c] = new Set();
        this[_d] = new Set();
        /** Associates a requestor with a desired polling mode */
        this.set = (pollingMode, requestorKey) => {
            const preexistingPollingMode = this.find(requestorKey);
            if (preexistingPollingMode) {
                if (preexistingPollingMode === pollingMode) {
                    return;
                }
                else {
                    this.delete(requestorKey);
                }
            }
            this[pollingMode].add(requestorKey);
        };
        /** Checks if the requestor is present in any of the Sets */
        this.find = (requestorKey) => POLLING_MODES.find((key) => this[key].has(requestorKey));
        /** Deletes the requestor from all Sets */
        this.delete = (requestorKey) => {
            POLLING_MODES.forEach((key) => {
                this[key].delete(requestorKey);
            });
        };
        /** Deletes all requestors from all Sets */
        this.clear = () => {
            POLLING_MODES.forEach((key) => {
                this[key].clear();
            });
        };
    }
    /** The accumulated size of controlRequestors */
    get size() {
        return POLLING_MODES.reduce((previous, key) => previous + this[key].size, 0);
    }
}
_a = types_1.PollingMode.None, _b = types_1.PollingMode.Callbacks, _c = types_1.PollingMode.Navigation, _d = types_1.PollingMode.All;
class PollingModeManager {
    /**
     * Resets the PollingModeManager to its foundational state, erasing all current requests.
     *
     * This function clears all the current requestors and their associated polling modes. The active
     * polling mode is then reverted back to the default mode.
     */
    static reset() {
        this.requestTracker.clear();
        // Return to the default polling mode
        this.setPollingMode(this.defaultPollingMode);
        console.info(`${consolePrefix} Reset successful`);
    }
}
exports.PollingModeManager = PollingModeManager;
_e = PollingModeManager;
PollingModeManager.requestTracker = new PollingModeRequestTracker();
PollingModeManager.defaultPollingMode = types_1.PollingMode.All;
PollingModeManager.initialized = false;
PollingModeManager.initialize = () => {
    if (_e.initialized) {
        return;
    }
    _e.setPollingMode(_e.defaultPollingMode);
    _e.initialized = true;
};
/**
 * Registers a request for a specific polling mode for an entity identified by the provided key.
 *
 * This method will consider the union of all requested polling modes from different components,
 * and the active polling mode is set to the most encompassing one (i.e., the mode which can
 * cater to all requests).
 *
 * If a key requests a different polling mode, the previously requested mode for that key
 * is superseded, and the active polling mode is recalculated based on all current requests.
 *
 * @param requestorKey - A unique identifier for the component or entity requesting the polling mode.
 * @param pollingMode - The desired polling mode to be set for the provided key.
 *
 * @returns {PollingMode} - The resultant active polling mode after considering all current requests.
 */
PollingModeManager.requestPollingMode = (requestorKey, pollingMode) => {
    const currentPollingMode = _e.requestTracker.find(requestorKey);
    _e.requestTracker.set(pollingMode, requestorKey);
    if (currentPollingMode) {
        if (currentPollingMode === pollingMode) {
            console.debug(`${consolePrefix} Redundant gamepad control requested by: ${requestorKey} - ${_e.requestTracker.size} gamepad requestors registered`);
        }
        else {
            console.debug(
            // eslint-disable-next-line max-len
            `${consolePrefix} Transition gamepad control from ${currentPollingMode} to ${pollingMode} requested by: ${requestorKey} - ${_e.requestTracker.size} gamepad requestors registered`);
        }
    }
    else {
        console.debug(`${consolePrefix} Gamepad control requested by: ${requestorKey} - ${_e.requestTracker.size} gamepad requestors registered`);
    }
    const newPollingMode = _e.getComputedPollingMode();
    _e.setPollingMode(newPollingMode);
    return newPollingMode;
};
/**
 * Revokes a previously requested polling mode for a component or entity identified by the key.
 *
 * Once a polling mode request is released for a specific key, the PollingModeManager recalculates
 * the active polling mode by considering the union of all remaining requested modes. If no other
 * entity has an active request, the active polling mode reverts to the default mode.
 *
 * @param requestorKey - The unique identifier for the component or entity that previously requested a polling mode.
 *
 * @returns {PollingMode} - The recalculated active polling mode after the release.
 */
PollingModeManager.releasePollingMode = (requestorKey) => {
    const isRedundant = _e.requestTracker.find(requestorKey) == null;
    _e.requestTracker.delete(requestorKey);
    if (isRedundant) {
        console.debug(`${consolePrefix} Redundant release gamepad control requested by: ${requestorKey} - ${_e.requestTracker.size} gamepad control requestors registered`);
    }
    else {
        console.debug(`${consolePrefix} Release gamepad control requested by: ${requestorKey} - ${_e.requestTracker.size} gamepad control requestors registered`);
    }
    const newPollingMode = _e.getComputedPollingMode();
    _e.setPollingMode(newPollingMode);
    return newPollingMode;
};
/**
 * Returns the currently set polling mode.
 */
PollingModeManager.getPollingMode = () => {
    return _e.pollingMode;
};
/**
 * Determine which polling mode to use based on the registered requestors.
 */
PollingModeManager.getComputedPollingMode = () => {
    if (_e.requestTracker[types_1.PollingMode.All].size > 0 ||
        (_e.requestTracker[types_1.PollingMode.Callbacks].size > 0 &&
            _e.requestTracker[types_1.PollingMode.Navigation].size > 0)) {
        return types_1.PollingMode.All;
    }
    if (_e.requestTracker[types_1.PollingMode.Callbacks].size > 0) {
        return types_1.PollingMode.Callbacks;
    }
    if (_e.requestTracker[types_1.PollingMode.Navigation].size > 0) {
        return types_1.PollingMode.Navigation;
    }
    if (_e.requestTracker[types_1.PollingMode.None].size > 0) {
        return types_1.PollingMode.None;
    }
    return _e.defaultPollingMode;
};
/**
 * Sets the current polling mode and updates library state accordingly
 *
 * @param pollingMode The new polling mode to set
 */
PollingModeManager.setPollingMode = (pollingMode) => {
    console.debug(`${consolePrefix} Setting polling mode to ${pollingMode}`);
    if (pollingMode === _e.pollingMode) {
        return;
    }
    _e.pollingMode = pollingMode;
    switch (pollingMode) {
        case types_1.PollingMode.All:
            console.log(consolePrefix, 'Enabling controller navigation');
            break;
        case types_1.PollingMode.None:
            console.log(consolePrefix, 'Disabling controller navigation');
            break;
        case types_1.PollingMode.Callbacks:
            console.log(consolePrefix, 'Enabling controller navigation for button callbacks only');
            break;
        case types_1.PollingMode.Navigation:
            console.log(consolePrefix, 'Enabling controller navigation for navigation only');
            break;
    }
    if (_e.isNavigationPollingEnabled()) {
        (0, input_processors_1.resetGamepadButtonState)(types_1.GamepadButton.A);
        (0, input_processors_1.resetGamepadButtonState)(types_1.GamepadButton.B);
        (0, foundation_1.startGamepadPolling)();
        (0, foundation_1.registerKeyboardEvents)();
        (0, foundation_1.registerPassiveMouseEvents)();
        if ((0, inputModeManager_1.doesDefaultToFocusDrivenInputMode)()) {
            (0, inputModeManager_1.returnToFocusDrivenInputMode)();
        }
    }
    else if (_e.isCallbackPollingEnabled()) {
        (0, foundation_1.registerPassiveMouseEvents)();
        if ((0, useRegisterInputHook_1.areGamepadButtonCallbacksRegistered)()) {
            (0, foundation_1.startGamepadPolling)();
        }
        else {
            (0, foundation_1.stopGamepadPolling)();
        }
        if ((0, useRegisterInputHook_1.areKeyboardKeyCallbacksRegistered)()) {
            (0, foundation_1.registerKeyboardEvents)();
        }
        else {
            (0, foundation_1.unregisterKeyboardEvents)();
        }
        (0, input_manager_1.clearNavigationTimeouts)();
    }
    else {
        (0, foundation_1.stopGamepadPolling)();
        (0, foundation_1.unregisterKeyboardEvents)();
        (0, foundation_1.unregisterPassiveMouseEvents)();
        (0, input_manager_1.clearInputState)();
    }
    gamepadEvents_1.GamepadEventEmitter.emit(gamepadEvents_1.PollingModeEvent.Change, pollingMode);
};
/**
 * Sets the default polling mode and recalculates the active polling mode based on current requests.
 *
 * @param pollingMode The new default pollingMode.
 */
PollingModeManager.setDefaultPollingMode = (pollingMode) => {
    _e.defaultPollingMode = pollingMode;
    _e.setPollingMode(_e.getComputedPollingMode());
};
/**
 * Retrieves the default polling mode.
 *
 * @returns The default polling mode.
 */
PollingModeManager.getDefaultPollingMode = () => {
    return _e.defaultPollingMode;
};
/**
 * Determines whether all types of polling are currently requested
 *
 * @returns true if the polling mode is set to PollingMode.All, false otherwise.
 */
PollingModeManager.isAllPollingEnabled = () => _e.pollingMode === types_1.PollingMode.All;
/**
 * Determines whether callback polling is currently requested
 *
 * @returns true if the polling mode is set to PollingMode.Callbacks, false otherwise.
 */
PollingModeManager.isCallbackPollingEnabled = () => {
    return _e.pollingMode === types_1.PollingMode.All || _e.pollingMode === types_1.PollingMode.Callbacks;
};
/**
 * Determines whether navigation polling is currently requested
 *
 * @returns true if the polling mode is set to PollingMode.Navigation, false otherwise.
 */
PollingModeManager.isNavigationPollingEnabled = () => {
    return _e.pollingMode === types_1.PollingMode.All || _e.pollingMode === types_1.PollingMode.Navigation;
};
//# sourceMappingURL=data:application/json;base64,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